import { ApolloClient, createHttpLink, InMemoryCache, split } from '@apollo/client/core'
import { WEBSOCKET_RECONNECT_INTERVAL_MS } from '@/config/config.ts'
import { DEFAULT_ENVIRONMENT, getApiUrlByEnv, getDefaultClientEnv, getWebsocketUrlByEnv, } from '@/config/env.const.ts'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { BackendErrorCode, Environment } from '@/enums.ts'
import { createApolloProvider } from '@vue/apollo-option'
import type { DocumentNode } from 'graphql/language/ast'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { onError } from '@apollo/client/link/error'
import { useUserStore } from '@/stores/user.store.ts'
import * as Sentry from '@sentry/vue'
import { useTranslations } from '@/composables/useTranslations.ts'
import { useToast } from 'vue-toastification'
import { SKIP_ERROR_TOAST_FOR_OPERATIONS } from '@/config/error.const.ts'
import { provideApolloClient } from '@vue/apollo-composable'

const splitTestFunc = ({ query }: { query: DocumentNode }) => {
  const definition = getMainDefinition(query)
  return (definition.kind === 'OperationDefinition' && definition.operation === 'subscription')
}

// TODO: REVALIDATE SOLUTION!
const errorLink = onError(({ operation, response, graphQLErrors }) => {
  if (DEFAULT_ENVIRONMENT !== Environment.Production) {
    console.error('GraphQL Error | operation:', operation)
    console.error('GraphQL Error | response:', response)
    console.error('GraphQL Error | graphQLErrors:', graphQLErrors)
    console.log("\n")
  }

  const { isAuthorized, getUserInfo } = useUserStore()
  const toast = useToast()
  const { t } = useTranslations()
  const userInfo = getUserInfo()

  Sentry.setUser({ id: isAuthorized ? userInfo.id as number : 'guest' })
  Sentry.setContext('operation', operation as unknown as Record<string, unknown>)
  Sentry.setContext('response', response as unknown as Record<string, unknown>)
  Sentry.setContext('errors', graphQLErrors as unknown as Record<string, unknown>)
  Sentry.captureMessage(JSON.stringify(graphQLErrors))

  if (!SKIP_ERROR_TOAST_FOR_OPERATIONS.includes(operation.operationName)) {
    const errorCodes: string[] = []

    if (Array.isArray(graphQLErrors)) {
      for (const error of graphQLErrors) {
        errorCodes.push(error.message)
      }
    }

    for (const errorCode of Array.from(new Set(errorCodes))) {
      if (Object.values(BackendErrorCode).includes(errorCode as BackendErrorCode)) {
        toast.error(t(errorCode))
      } else {
        console.error('GraphqlErrorLink | Unknown error', errorCode)
        toast.error(BackendErrorCode.UnknownError)
      }
    }
  }
})

export const WSLinkStore = Object.freeze(
  Object
    .values(Environment)
    .reduce<Record<Environment, GraphQLWsLink>>((acc, env: Environment)=> {
      acc[env] = new GraphQLWsLink(createClient({
        url: getWebsocketUrlByEnv(env),
        retryAttempts: 100,
        shouldRetry (err: unknown): boolean {
          console.warn('shouldRetry | err:', err)
          return true
        },
        async retryWait(retries: number): Promise<void>  {
          await new Promise((resolve) => setTimeout(resolve, WEBSOCKET_RECONNECT_INTERVAL_MS))
        }
      }))

      return acc
    }, {} as Record<Environment, GraphQLWsLink>)
)

export const ApolloClientStore = Object
  .values(Environment)
  .reduce<Record<Environment, ApolloClient<any>>>((acc, env: Environment)=> {
    const httpOptions = {
      uri: getApiUrlByEnv(env),
      credentials: 'include'
    }

    acc[env] = new ApolloClient({
      link: split(
        splitTestFunc,
        split(
          splitTestFunc,
          WSLinkStore[env],
          errorLink.concat(createHttpLink(httpOptions))
        ),
        errorLink.concat(createUploadLink(httpOptions))
      ),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'network-only',
        },
      },
    })

    return acc
  }, {} as Record<Environment, ApolloClient<any>>)

export const apolloProvider = createApolloProvider({
  clients: ApolloClientStore,
  defaultClient: ApolloClientStore[getDefaultClientEnv()]
})

export function setDefaultApolloClient(env: Environment) {
  apolloProvider.defaultClient = ApolloClientStore[env]
  provideApolloClient(apolloProvider.defaultClient)
}

